/**
 * Simulation Engine
 * 
 * Core logic for simulating virtual agents with behaviors in real-time.
 * Handles wait behaviors (with/without location) and datasetview behaviors.
 */

/**
 * Initialize agent states for simulation
 * @param {Array} virtualAgents - Array of virtual agent objects with behaviors
 * @returns {Array} Initialized agent states
 */
export const initializeAgentStates = (virtualAgents) => {
  return virtualAgents.map((agent, index) => ({
    agentIndex: index,
    agent: agent,
    currentBehaviorIndex: 0,
    behaviorStartTime: 0, // Simulation time when current behavior started
    currentPosition: null,
    currentDatasetLocations: null,
    hasFirstLocation: false,
    completed: false
  }));
};

/**
 * Calculate the current position for an agent based on simulation time
 * @param {Object} agentState - Current agent state
 * @param {number} simulationTime - Current simulation time in seconds
 * @returns {Object|null} Position object with lat/lon or null if no position
 */
export const calculateAgentPosition = (agentState, simulationTime) => {
  const { agent, currentBehaviorIndex, behaviorStartTime, currentDatasetLocations } = agentState;
  const behaviors = agent.behaviors || [];
  
  // Agent has completed all behaviors - return last known position
  if (currentBehaviorIndex >= behaviors.length) {
    return agentState.currentPosition;
  }

  const behavior = behaviors[currentBehaviorIndex];
  const behaviorElapsedTime = simulationTime - behaviorStartTime;

  if (behavior.type === 'wait') {
    // Wait behavior
    if (behavior.latitude !== void 0 && behavior.longitude !== void 0) {
      // Wait with location - agent is at this location
      return { lat: behavior.latitude, lon: behavior.longitude };
    } else {
      // Wait without location - no position yet
      return null;
    }
  } else if (behavior.type === 'datasetview') {
    // Dataset view behavior
    if (!currentDatasetLocations || currentDatasetLocations.length === 0) {
      return agentState.currentPosition; // Keep last position if no dataset loaded
    }

    const datasetStartTime = behavior.start_time;
    const datasetEndTime = behavior.end_time;
    const datasetDuration = datasetEndTime - datasetStartTime;
    
    // Time within the dataset (0 to datasetDuration)
    const timeInDataset = behaviorElapsedTime;
    
    if (timeInDataset < 0) {
      return agentState.currentPosition; // Before dataset starts
    }
    
    if (timeInDataset >= datasetDuration) {
      // Past end of dataset - return last location in dataset
      const lastLocation = currentDatasetLocations[currentDatasetLocations.length - 1];
      return { lat: lastLocation.lat, lon: lastLocation.lon };
    }

    // Find location at this time in the dataset
    const targetTimestamp = datasetStartTime + timeInDataset;
    
    // Find closest location by timestamp
    let closestLocation = currentDatasetLocations[0];
    let minDiff = Math.abs(currentDatasetLocations[0].timestamp - targetTimestamp);
    
    for (let i = 1; i < currentDatasetLocations.length; i++) {
      const diff = Math.abs(currentDatasetLocations[i].timestamp - targetTimestamp);
      if (diff < minDiff) {
        minDiff = diff;
        closestLocation = currentDatasetLocations[i];
      }
    }
    
    return { lat: closestLocation.lat, lon: closestLocation.lon };
  }

  return agentState.currentPosition;
};

/**
 * Update agent states based on current simulation time
 * Handles behavior transitions and position updates
 * @param {Array} agentStates - Current agent states
 * @param {number} simulationTime - Current simulation time in seconds
 * @returns {Array} Updated agent states
 */
export const updateAgentStates = (agentStates, simulationTime) => {
  return agentStates.map(agentState => {
    const { agent, currentBehaviorIndex, behaviorStartTime } = agentState;
    const behaviors = agent.behaviors || [];
    
    // Agent has completed all behaviors
    if (currentBehaviorIndex >= behaviors.length) {
      return {
        ...agentState,
        completed: true
      };
    }

    const behavior = behaviors[currentBehaviorIndex];
    const behaviorElapsedTime = simulationTime - behaviorStartTime;

    // Check if current behavior is complete and transition to next
    let newBehaviorIndex = currentBehaviorIndex;
    let newBehaviorStartTime = behaviorStartTime;
    let newCurrentPosition = agentState.currentPosition;
    let newHasFirstLocation = agentState.hasFirstLocation || false;

    if (behavior.type === 'wait') {
      if (behaviorElapsedTime >= behavior.duration) {
        // Wait behavior complete - move to next behavior
        newBehaviorIndex = currentBehaviorIndex + 1;
        newBehaviorStartTime = simulationTime;
        // Keep current position when transitioning
      } else {
        // Still waiting - update position if location is specified
        if (behavior.latitude !== void 0 && behavior.longitude !== void 0) {
          newCurrentPosition = { lat: behavior.latitude, lon: behavior.longitude };
          newHasFirstLocation = true;
        }
      }
    } else if (behavior.type === 'datasetview') {
      // Duration in seconds (timestamps are Unix timestamps in seconds)
      const datasetDuration = behavior.end_time - behavior.start_time;
      if (behaviorElapsedTime >= datasetDuration) {
        // Dataset view complete - move to next behavior
        newBehaviorIndex = currentBehaviorIndex + 1;
        newBehaviorStartTime = simulationTime;
        // Keep last position from dataset
        if (agentState.currentDatasetLocations && agentState.currentDatasetLocations.length > 0) {
          const lastLocation = agentState.currentDatasetLocations[agentState.currentDatasetLocations.length - 1];
          newCurrentPosition = { lat: lastLocation.lat, lon: lastLocation.lon };
          newHasFirstLocation = true;
        }
      } else {
        // Still playing dataset - calculate current position
        const position = calculateAgentPosition({ ...agentState, currentBehaviorIndex, behaviorStartTime }, simulationTime);
        if (position) {
          newCurrentPosition = position;
          newHasFirstLocation = true;
        }
      }
    }

    return {
      ...agentState,
      currentBehaviorIndex: newBehaviorIndex,
      behaviorStartTime: newBehaviorStartTime,
      currentPosition: newCurrentPosition,
      hasFirstLocation: newHasFirstLocation
    };
  });
};

/**
 * Calculate maximum simulation time based on all agent behaviors
 * @param {Array} virtualAgents - Array of virtual agents
 * @returns {number} Maximum simulation time in seconds
 */
export const calculateMaxSimulationTime = (virtualAgents) => {
  let maxTime = 0;
  
  virtualAgents.forEach(agent => {
    const behaviors = agent.behaviors || [];
    let agentTime = 0;
    
    behaviors.forEach(behavior => {
      if (behavior.type === 'wait') {
        agentTime += (behavior.duration || 0);
      } else if (behavior.type === 'datasetview') {
        if (behavior.start_time && behavior.end_time) {
          agentTime += (behavior.end_time - behavior.start_time);
        }
      }
    });
    
    maxTime = Math.max(maxTime, agentTime);
  });
  
  return maxTime || 100; // Default to 100 seconds if no behaviors
};

/**
 * Process a single simulation step
 * Updates all agent states and calculates positions
 * @param {Array} agentStates - Current agent states
 * @param {number} simulationTime - Current simulation time in seconds
 * @returns {Array} Updated agent states with positions
 */
export const processSimulationStep = (agentStates, simulationTime) => {
  // First update behavior states (transitions, completions)
  const updatedStates = updateAgentStates(agentStates, simulationTime);
  
  // Then calculate positions for all agents
  return updatedStates.map(state => {
    const position = calculateAgentPosition(state, simulationTime);
    return {
      ...state,
      currentPosition: position,
      hasFirstLocation: state.hasFirstLocation || (position !== null)
    };
  });
};
